import * as React from "react";
import { AtSign, Loader2 } from "lucide-react";

import { Textarea, type TextareaProps } from "@/components/ui/textarea";
import { useMentionableUsers, type MentionableUser } from "@/hooks/useForum";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// MentionTextarea — a Textarea with inline @username autocomplete.
//
// Detects an in-progress @token at the caret (e.g. "hey @mik|") and pops a
// small list of matching users below the field. Pick with click or Enter,
// dismiss with Escape. Mirrors the server MENTION_RE: handles are
// `[a-zA-Z][a-zA-Z0-9.\-]{1,40}` — so we lower-case + join first/last on
// dot when building the inserted token.
//
// Notes:
//  - Stays uncontrolled-style: we still drive everything through `value` +
//    `onChange` so the parent can keep its own state.
//  - The popup is anchored relative to a wrapping div, so the host page
//    doesn't need a portal/popover primitive — we don't have one installed.
// ---------------------------------------------------------------------------

export interface MentionTextareaProps extends TextareaProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

// Build the inserted mention token from a user. We prefer first.last so the
// rendered `@first.last` actually maps to a single human; falls back to the
// local part of their email if the names have no usable chars.
function mentionHandle(u: MentionableUser): string {
  const clean = (s: string) =>
    s.toLowerCase().replace(/[^a-z0-9.\-]/g, "").replace(/^[^a-z]+/, "");
  const first = clean(u.first_name);
  const last  = clean(u.last_name);
  if (first && last) return `${first}.${last}`;
  if (first) return first;
  if (last)  return last;
  return clean((u.email || "").split("@")[0] || "user");
}

// Pull the in-progress `@token` immediately before the caret. Returns
// `{ query, start }` where `start` is the offset of the `@` so we can
// splice it out cleanly when picking. Null when not actively typing one.
function detectMentionAtCaret(text: string, caret: number): { query: string; start: number } | null {
  const head = text.slice(0, caret);
  // `@` must be at the very start or follow whitespace / opening bracket
  // so we don't autocomplete inside an email address.
  const m = /(?:^|[\s(\[{])@([a-zA-Z0-9.\-]*)$/.exec(head);
  if (!m) return null;
  const at = head.lastIndexOf("@", caret);
  if (at === -1) return null;
  const query = m[1] ?? "";
  // Cap the query — anything longer is almost certainly not an intentional
  // mention, and the backend caps the token at 40 chars anyway.
  if (query.length > 40) return null;
  return { query, start: at };
}

const MentionTextarea = React.forwardRef<HTMLTextAreaElement, MentionTextareaProps>(
  ({ value, onChange, onKeyDown, className, ...rest }, forwardedRef) => {
    const innerRef = React.useRef<HTMLTextAreaElement | null>(null);
    // Bridge forwarded + inner ref so the parent can still focus().
    React.useImperativeHandle(forwardedRef, () => innerRef.current as HTMLTextAreaElement);

    const [mention, setMention] = React.useState<{ query: string; start: number } | null>(null);
    const [active,  setActive]  = React.useState(0);

    const { data: users, isFetching } = useMentionableUsers(mention?.query ?? "");
    const matches = mention ? (users ?? []) : [];
    const open    = mention !== null && (matches.length > 0 || isFetching);

    // Reset highlight when the candidate list changes shape.
    React.useEffect(() => { setActive(0); }, [mention?.query, matches.length]);

    function refreshMention(t: HTMLTextAreaElement) {
      const found = detectMentionAtCaret(t.value, t.selectionStart ?? t.value.length);
      setMention(found);
    }

    function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
      onChange(e);
      // selectionStart on the same tick is the post-change caret position.
      refreshMention(e.target);
    }

    function pick(u: MentionableUser) {
      if (!mention) return;
      const t = innerRef.current;
      if (!t) return;
      const handle = mentionHandle(u);
      const before = t.value.slice(0, mention.start);
      const after  = t.value.slice(t.selectionStart ?? t.value.length);
      const insert = `@${handle} `;
      const next   = before + insert + after;
      // Set value via the native setter so React's synthetic onChange
      // observer still fires — directly assigning `.value` on a controlled
      // input gets reverted on the next render.
      const setter = Object.getOwnPropertyDescriptor(
        window.HTMLTextAreaElement.prototype, "value",
      )?.set;
      setter?.call(t, next);
      const caret  = before.length + insert.length;
      t.dispatchEvent(new Event("input", { bubbles: true }));
      // Move caret to just after the inserted handle + space.
      requestAnimationFrame(() => {
        t.focus();
        t.setSelectionRange(caret, caret);
      });
      setMention(null);
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
      if (open) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setActive((i) => (matches.length ? (i + 1) % matches.length : 0));
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setActive((i) => (matches.length ? (i - 1 + matches.length) % matches.length : 0));
          return;
        }
        // Tab always picks (it's the unambiguous "I want this mention" key).
        // Enter only picks if (a) there's a match AND (b) the user is
        // actually mid-token (caret right after the @<query>). Otherwise
        // Enter falls through so the form's submit shortcut wins — the
        // popup never used to close cleanly when the caret moved away
        // from the @, so Enter would steal the send unexpectedly.
        if (e.key === "Tab") {
          if (matches[active]) {
            e.preventDefault();
            pick(matches[active]);
            return;
          }
        }
        if (e.key === "Enter") {
          const t = e.currentTarget;
          const caret = t.selectionStart ?? t.value.length;
          const stillAtMention = detectMentionAtCaret(t.value, caret);
          if (stillAtMention && matches[active]) {
            e.preventDefault();
            pick(matches[active]);
            return;
          }
          // Else — caret has moved past the mention token (or there's
          // no real match). Let the parent's onKeyDown decide.
        }
        if (e.key === "Escape") {
          e.preventDefault();
          setMention(null);
          return;
        }
      }
      onKeyDown?.(e);
    }

    function handleKeyUp(e: React.KeyboardEvent<HTMLTextAreaElement>) {
      // Arrow keys / clicks change the caret without firing onChange —
      // re-scan so the popup follows the caret.
      if (e.key.startsWith("Arrow") || e.key === "Home" || e.key === "End") {
        refreshMention(e.currentTarget);
      }
    }

    function handleClick(e: React.MouseEvent<HTMLTextAreaElement>) {
      refreshMention(e.currentTarget);
    }

    function handleBlur() {
      // Defer so a click on a suggestion still registers before close.
      setTimeout(() => setMention(null), 120);
    }

    return (
      <div className="relative">
        <Textarea
          ref={innerRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          onClick={handleClick}
          onBlur={handleBlur}
          className={className}
          {...rest}
        />

        {open && (
          <div
            className={cn(
              "absolute left-0 right-0 top-full z-30 mt-1 max-h-64 overflow-y-auto",
              "rounded-md border border-border/60 bg-popover p-1 text-sm shadow-md",
            )}
            // Keep focus on the textarea — let the click below trigger pick().
            onMouseDown={(e) => e.preventDefault()}
          >
            {isFetching && matches.length === 0 ? (
              <div className="flex items-center gap-2 px-2 py-2 text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Looking up people…
              </div>
            ) : matches.length === 0 ? (
              <div className="px-2 py-2 text-muted-foreground">No matches.</div>
            ) : (
              <ul role="listbox" aria-label="Mention suggestions">
                {matches.map((u, i) => {
                  const handle = mentionHandle(u);
                  const isActive = i === active;
                  return (
                    <li key={u.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        onMouseEnter={() => setActive(i)}
                        onClick={() => pick(u)}
                        className={cn(
                          "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left",
                          isActive ? "bg-accent text-accent-foreground" : "hover:bg-accent/60",
                        )}
                      >
                        <AtSign className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <span className="min-w-0 flex-1 truncate">
                          <span className="font-medium">{u.full_name}</span>
                          <span className="ml-2 text-xs text-muted-foreground">@{handle}</span>
                        </span>
                        <span className="hidden truncate text-xs text-muted-foreground sm:inline">
                          {u.email}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    );
  },
);
MentionTextarea.displayName = "MentionTextarea";

export { MentionTextarea };
